<template>
    <div class="o-m-map m-map">
        <OArView
            v-if="isArEnabled"
            :compassDegree="compassDegree"
            :class="['m-map-ar-view', arViewActiveClass, arViewFullScreenClass]"
            :is-ar-enabled="arMode"
            @closeArView="closeArView"
            @onArFullScreen="handleArFullScreen"
        />
        <ODMapController />
        <MZoomControls v-if="!arFullScreen"/>
        <ACompanyInfo
            v-if="isShowExtras"
        />
        <ACompassButton
            v-if="!arFullScreen"
            v-model="gyroScopeBtnState"
            :rotate="compassDegree"
        />
        <AArButton
            v-if="isArButton"
            class="m-map-ar-button"
            @onClick="handleArClick"
        />
        <OMWarningModal />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import AArButton from '../../atoms/a-ar-button';
import OMWarningModal from '../../organisms/o-m-warning-modal';
import ACompanyInfo from '../../atoms/a-company-info/a-company-info';
import ACompassButton from '../../atoms/a-compass-button/a-compass-button';
import MZoomControls from '../../molecules/m-zoom-controls/m-zoom-controls';

import ODMapController from "../../organisms/o-d-map-controller/o-d-map-controller";

import {
    SET_GYROSCOPE_STATE,
    TURN_ON_DEVICE_ORIENTATION_EVENTS,
    DISABLE_TOUCH_HORIZONTAL_ROTATION
} from '@/shared/components/map-viewer/constants/map-commands';
import { Storage } from '../../../utils/services/storage-service';

import {
    MOBILE_ROOT,
    MOBILE_GUIDE
} from '../../../utils/constants/pages';
import { UI_ON_CAMERA_UPDATE } from "@/shared/components/map-viewer/constants/map-events";
import {
    EVENTS,
    SHOW_WARNING_MODAL
} from "../../../utils/constants/bus-events-constants";
import {
    WARNING_MODAL_PERMISSIONS_DISCLAIMER_CONFIG,
    WARNING_MODAL_PERMISSIONS_INSTRUCTIONS_CONFIG
} from "../../organisms/o-m-warning-modal/constants";

export default {
    name: 'OMMAp',
    components: {
        OArView: () => import('@/components/organisms/o-ar-view/o-ar-view'),
        AArButton,
        ACompanyInfo,
        MZoomControls,
        OMWarningModal,
        ACompassButton,
        ODMapController
    },
    data() {
        return {
            gyroScopeBtnState: true,
            compassDegree: 0,
            arMode: false,
            arFullScreen: false
        }
    },
    computed: {
        ...mapGetters([
            'getMapConfigProp'
        ]),
        isShowExtras() {
            return this.$route.name.includes(MOBILE_ROOT)
        },
        isMapScreen() {
            return this.$route.name.includes(MOBILE_GUIDE)
        },
        compassCorrectionValue() {
            return this.getMapConfigProp('compass_correction', 0);
        },
        arViewActiveClass() {
            return this.arMode ? 'm-map-ar-view-active' : null;
        },
        arViewFullScreenClass() {
            return this.arFullScreen ? 'm-map-ar-view-full-screen' : null;
        },
        isArEnabled(){
            return this.getMapConfigProp('features.ar.enabled', false);
        },
        isArButton() {
            return this.isArEnabled && !this.arMode && this.$route.name === MOBILE_GUIDE;
        }
    },
    mounted() {
        this.initListeners();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    watch: {
        gyroScopeBtnState: {
            handler(value) {
                if (value) this.modelOrientation();
                else this.compassOrientation();
            },
            immediate: true
        }
    },
    methods: {
        deactivateEventsListeners() {
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$off(EVENTS.DISABLE_AR_VIEW, this.closeArView);
            this.$bus.$off(EVENTS.MODALS.PROCEED_TO_REQUEST_PERMISSIONS, this.proceedArPermissions);
        },
        initListeners() {
            this.$bus.$on(EVENTS.DISABLE_AR_VIEW, this.closeArView);
            this.$bus.$on(EVENTS.MODALS.PROCEED_TO_REQUEST_PERMISSIONS, this.proceedArPermissions);
        },
        modelOrientation() {
            this.$bus.$on(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, false);
            this.$bus.$emit(SET_GYROSCOPE_STATE, false);
        },
        updateOrientation() {
            if(document.viewApi)
                this.compassDegree = this.compassCorrectionValue +  document.viewApi.camera_controller.current_orientation
        },
        async compassOrientation() {
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.updateOrientation);
            this.$bus.$emit(SET_GYROSCOPE_STATE, true);
            this.$bus.$emit(DISABLE_TOUCH_HORIZONTAL_ROTATION, true);
            this.$bus.$emit(TURN_ON_DEVICE_ORIENTATION_EVENTS, true);

            const isOrientationEventSupport = typeof(DeviceOrientationEvent) !== "undefined" && typeof(DeviceOrientationEvent.requestPermission) === "function";

            if (isOrientationEventSupport) {
                try {
                    const response = await DeviceOrientationEvent.requestPermission();

                    if (response === 'granted') {
                        window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
                    }

                } catch(error) {
                    console.warn(error);
                }
            } else {
                window.addEventListener('deviceorientation', this.addDeviceOrientationListener);
            }
        },
        addDeviceOrientationListener(event) {
            const IOS = navigator.platform.indexOf('iPhone') !== -1;
            if (IOS) {
                this.compassDegree = Math.round(event.webkitCompassHeading);
            } else {
                this.compassDegree = Math.round(event.alpha);
            }

            if (this.compassDegree < 0) { this.compassDegree += 360; }
            if (this.compassDegree > 360) { this.compassDegree -= 360; }
        },
        handleArClick() {
            const isPermissions = Storage.local.get('motionEventsPermissions') || false;
            const isPermissionsDenied = isPermissions === 'denied';
            const isPermissionsGranted = isPermissions === 'granted';

            const config = isPermissionsDenied ? WARNING_MODAL_PERMISSIONS_INSTRUCTIONS_CONFIG : WARNING_MODAL_PERMISSIONS_DISCLAIMER_CONFIG;

            if (isPermissionsGranted) {
                this.proceedArPermissions();
            } else {
                this.$bus.$emit(SHOW_WARNING_MODAL, config);
            }
        },
        proceedArPermissions() {
            this.arMode = true;
            this.gyroScopeBtnState = false;
        },
        closeArView() {
            this.arMode = false;
            this.arFullScreen = false;
        },
        handleArFullScreen() {
            this.arFullScreen = !this.arFullScreen;
        }
    }
}
</script>

<style lang="scss" src="./o-m-map.scss" />
